import { FC, useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { Props } from './type';
import ChapterDetails from './components/ChapterDetails';
import { Chapter } from '@/components/tour/game/OpenGameBook/components/ChapterDetails/type';
import { Root, Header, List } from '@/components/tour/game/OpenGameBook/styled';
import Icon from '@/units/Icon';
import { useBasicStore, useUIStore } from '../../../../store';
import { BannerHorizontalViewOnly } from '../BannerHorizontalViewOnly/BannerHorizontalViewOnly';
import { TourType } from '@/_models/Tour';
import { GameBookLocationType } from '@/components/tour/game/gameBook/types';
import { parseTourLink } from '@/_helpers/base';
import DeviceManager from '@/components/util/deviceManager';
import { ClickAwayListener } from '@mui/material';

export const OpenGameBook: FC<Props & { children?: any }> = ({
  book,
  show,
  unlockedChapterIds,
  onClose,
  moveToSweep,
  tourType,
}) => {
  if (!show) return null;

  const translation = useBasicStore((state) => state.translationData);
  const sbIsOpen = useUIStore((state) => state.sidebarOpen);

  const { backgroundColor, backgroundOpacity, textColor, buttonColor, chapterLocked } = book;

  const [activeData, setActiveData] = useState<Chapter>();

  const isEcommerce = tourType === TourType.ECommerce;

  const isHorizontalMobileView = DeviceManager.isHorizontalMobileView();

  const isChapterLocked = (chapterId: number) => {
    if (!chapterLocked) return false;
    return !unlockedChapterIds.includes(chapterId);
  };

  const openChapter = (chapterId: number) => {
    let data;

    if (chapterId === 0) {
      data = {
        title: translation?.experienceCover || 'Cover',
        description: book.description,
        url: book.img
          ? `${process.env.NEXT_PUBLIC_CDN}/games/${book.gameId}/book/${book.id}/${book.img}`
          : undefined,
        moveToSweepUuid: undefined,
      };
    } else if (!isChapterLocked(chapterId)) {
      const chapter = book.chapters.find((x) => x.id === chapterId);
      data = {
        // ...chapter,
        locationType: chapter.locationType,
        locationTourLink: chapter.locationTourLink,
        title: chapter.title,
        description: chapter.description,
        url: chapter.img
          ? `${process.env.NEXT_PUBLIC_CDN}/games/${book.gameId}/book/${book.id}/chapter/${chapter.id}/${chapter.img}`
          : undefined,
        moveToSweepUuid: chapter.sweepUuid,
      };
    }

    if (data) {
      setActiveData(data);
    }
  };

  useEffect(() => {
    try {
      if (activeData) {
        if (activeData.locationType === GameBookLocationType.sweep && activeData.moveToSweepUuid) {
          moveToSweep(activeData.moveToSweepUuid);
        } else if (
          activeData.locationType === GameBookLocationType.tourLink &&
          activeData.locationTourLink
        ) {
          const parsedLink = parseTourLink(activeData.locationTourLink);
          if (parsedLink) {
            moveToSweep(parsedLink.s, { x: parsedLink.x, y: parsedLink.y });
          }
        }
        setActiveData(undefined);
      }
    } catch (err) {
      console.error('gameBook ChapterDetails handleMoveToSweep error');
    }
  }, [activeData, moveToSweep, onClose]);

  const eCommerceStyle = {
    top: '100px',
    marginTop: 0,
    right: sbIsOpen ? '180px' : '50px',
  };
  //timeout will prevent reopen configs closed by sidebar configs icon
  const handleClickAway = (ev) => {
    if (isEcommerce) {
      window.setTimeout(() => {
        onClose();
      }, 100);
    }
  };

  //disable for mobile
  if (DeviceManager.isMobile()) return null;
  return (
    <>
      <BannerHorizontalViewOnly show={show} onClose={onClose} />
      <ClickAwayListener mouseEvent={'onMouseUp'} onClickAway={handleClickAway}>
        <div className="handleClickAwayNotWorksWithoutThisWrap">
          <Root
            background={backgroundColor}
            opacity={backgroundOpacity}
            color={textColor}
            style={isEcommerce ? eCommerceStyle : undefined}
          >
            <Header>
              <h2>{book?.description || translation?.expJournBook || 'Journey book'}</h2>
              <Icon.Close onClick={onClose} />
            </Header>
            <List
              buttonColor={buttonColor}
              color={textColor}
              isHorizontalMobileView={isHorizontalMobileView}
            >
              {book.img && (
                <li key="0" className="chapter" onClick={() => openChapter(0)}>
                  {translation?.experienceCover || 'Cover'}
                </li>
              )}
              {sortBy(book.chapters || [], 'order').map((chapter) => (
                <li
                  className="d-flex justify-content-between align-center chapter"
                  key={chapter.id}
                  onClick={() => openChapter(chapter.id)}
                >
                  {chapter.title}
                  {isChapterLocked(chapter.id) && <Icon.Lock fontSize="inherit" />}
                </li>
              ))}
            </List>
            <ChapterDetails
              book={book}
              chapter={activeData}
              onClose={() => setActiveData(undefined)}
              moveToSweep={moveToSweep}
            />
          </Root>
        </div>
      </ClickAwayListener>
    </>
  );
};
